import React, { useEffect, useMemo, useState } from 'react';
import { useNavigate, useParams, useSearchParams, useLocation } from 'react-router-dom';
import useSWR from 'swr';
import { fetcher } from '../../../services/fetcher';
import { useNavigateBack, getLocationReturnPath } from '../../../hooks/useNavigateBack';
import { useTheme } from '../../../contexts/ThemeContext';
import { useNotification } from '../../../contexts/NotificationContext';
import { cn } from '../../../lib/utils';
import { formatDate as formatDateGlobal } from '../../../config/appConfig';
import {
  Button,
  ChevronLeftIcon,
  DateInput,
  Label,
  NumberInput,
  Select,
  RefreshIcon,
} from '../../../components/ui';
import { acctFieldLabel } from '../shared/typography';
import { createAccCurrencyRate, fetchAccCurrencyRateOnline, updateAccCurrencyRate } from '../../../services/mutations';
import { useAccCurrencyRate } from './useData';
import { moduleConfig } from './config';
import { formatCurrencyCodeSymbol, listRateCurrencies } from './currencyDisplay';

type Currency = { id: number; code: string; name: string; symbol?: string | null };

const todayIso = () => new Date().toISOString().slice(0, 10);

const formatRate = (value: number | string | null | undefined) => {
  const n = Number(value);
  if (!Number.isFinite(n) || n <= 0) return null;
  return n.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 6 });
};

const FieldHint: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const { colors } = useTheme();
  return <p className={cn('text-xs mt-1.5 leading-relaxed', colors.textMuted)}>{children}</p>;
};

export const Form: React.FC = () => {
  const { colors } = useTheme();
  const { showError, showSuccess } = useNotification();
  const navigate = useNavigate();
  const location = useLocation();
  const goBack = useNavigateBack(moduleConfig.module.route.list, getLocationReturnPath(location.state));
  const { id } = useParams<{ id: string }>();
  const [searchParams] = useSearchParams();
  const editId = id && id !== 'new' ? parseInt(id, 10) : NaN;
  const isEdit = Number.isInteger(editId);

  const { item, isLoading } = useAccCurrencyRate(isEdit ? editId : 0);
  const { data: currenciesRaw } = useSWR<Currency[] | { data: Currency[] }>('/currencies', fetcher);

  const currencies = useMemo(
    () =>
      listRateCurrencies(
        (Array.isArray(currenciesRaw) ? currenciesRaw : currenciesRaw?.data ?? [])
          .slice()
          .sort((a, b) => a.code.localeCompare(b.code)),
      ),
    [currenciesRaw],
  );

  const [currencyId, setCurrencyId] = useState('');
  const [rateDate, setRateDate] = useState(todayIso);
  const [rate, setRate] = useState('');
  const [saving, setSaving] = useState(false);
  const [fetchingOnline, setFetchingOnline] = useState(false);
  const [onlineSourceDate, setOnlineSourceDate] = useState<string | null>(null);
  const [hydrated, setHydrated] = useState(!isEdit);

  useEffect(() => {
    if (isEdit || hydrated) return;
    const fromQuery = searchParams.get('currencyId');
    if (fromQuery && Number.isFinite(Number(fromQuery))) {
      const id = Number(fromQuery);
      if (currencies.some((c) => c.id === id)) {
        setCurrencyId(String(id));
      } else if (currencies[0]) {
        setCurrencyId(String(currencies[0].id));
      }
    } else if (currencies[0]) {
      setCurrencyId(String(currencies[0].id));
    }
    setHydrated(true);
  }, [isEdit, hydrated, searchParams, currencies]);

  useEffect(() => {
    if (!isEdit || !item) return;
    setCurrencyId(String(item.currencyId));
    setRateDate(item.rateDate?.slice(0, 10) ?? todayIso());
    setRate(item.rate != null ? String(item.rate) : '');
    setOnlineSourceDate(null);
    setHydrated(true);
  }, [isEdit, item]);

  useEffect(() => {
    setOnlineSourceDate(null);
  }, [currencyId, rateDate]);

  const currencyOptions = useMemo(
    () => [
      { value: '', label: 'Select currency' },
      ...currencies.map((c) => ({ value: String(c.id), label: c.name || formatCurrencyCodeSymbol(c) })),
    ],
    [currencies],
  );

  const selectedCurrency = useMemo(
    () => currencies.find((c) => String(c.id) === currencyId) ?? item?.currency ?? null,
    [currencies, currencyId, item?.currency],
  );

  const rateNumber = Number(rate);
  const rateValid = Number.isFinite(rateNumber) && rateNumber > 0;
  const formattedRate = formatRate(rate);
  const exampleLocal = 100;
  const exampleUsd = rateValid ? exampleLocal * rateNumber : null;

  const formTitle = isEdit ? moduleConfig.form.edit.title : moduleConfig.form.new.title;
  const formSubtitle = isEdit ? moduleConfig.form.edit.subtitle : moduleConfig.form.new.subtitle;
  const submitText = isEdit ? moduleConfig.form.edit.submitText : moduleConfig.form.new.submitText;

  const handleFetchOnline = async () => {
    if (!selectedCurrency?.code) {
      showError('Select a currency first.');
      return;
    }
    if (!rateDate) {
      showError('Enter a rate date first.');
      return;
    }

    setFetchingOnline(true);
    try {
      const result = await fetchAccCurrencyRateOnline(selectedCurrency.code, rateDate);
      setRate(String(result.rate));
      setOnlineSourceDate(result.sourceDate);
      const dateNote =
        result.sourceDate !== result.requestedDate
          ? ` (ECB rate for ${formatDateGlobal(result.sourceDate)})`
          : '';
      showSuccess(`Fetched online rate for ${selectedCurrency.code}${dateNote}.`);
    } catch (err: unknown) {
      const message =
        (err as { response?: { data?: { message?: string } } })?.response?.data?.message
        ?? (err as Error)?.message
        ?? 'Could not fetch rate online.';
      showError(message);
    } finally {
      setFetchingOnline(false);
    }
  };

  const onSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!currencyId) {
      showError('Select a currency.');
      return;
    }
    if (!rateDate) {
      showError('Enter a rate date.');
      return;
    }
    if (!rateValid) {
      showError('Enter a valid rate greater than zero.');
      return;
    }

    setSaving(true);
    try {
      const payload = {
        currencyId: Number(currencyId),
        rateDate,
        rate: rateNumber,
      };
      if (isEdit) {
        await updateAccCurrencyRate(editId, payload);
        showSuccess('Currency rate updated.');
        navigate(moduleConfig.module.route.detail(editId));
      } else {
        const created = await createAccCurrencyRate(payload);
        showSuccess('Currency rate created.');
        navigate(moduleConfig.module.route.detail(created.id));
      }
    } catch (err: unknown) {
      const message =
        (err as { response?: { data?: { message?: string } } })?.response?.data?.message
        ?? (err as Error)?.message
        ?? moduleConfig.form.errorMessage;
      showError(message);
    } finally {
      setSaving(false);
    }
  };

  if (isEdit && (isLoading || !hydrated)) {
    return (
      <div className={cn('flex items-center justify-center flex-1 min-h-[240px] text-sm', colors.textMuted)}>
        {moduleConfig.form.loadingText}
      </div>
    );
  }

  return (
    <div className="h-full flex flex-col min-h-0 p-4">
      <form
        onSubmit={onSubmit}
        className={cn(
          'flex-1 flex flex-col min-h-0 overflow-hidden rounded-xl border shadow-sm',
          colors.surface,
          colors.border,
        )}
      >
        <div
          className={cn(
            'flex-shrink-0 flex flex-wrap items-center justify-between gap-3 px-4 sm:px-6 py-3 border-b',
            colors.border,
            colors.surfaceHover,
          )}
        >
          <div className="flex items-center gap-3 min-w-0">
            <Button type="button" variant="ghost" size="icon" title="Back" aria-label="Back" onClick={goBack}>
              <ChevronLeftIcon className="h-5 w-5" />
            </Button>
            <div className="min-w-0">
              <h1 className={cn('text-base font-semibold truncate', colors.text)}>{formTitle}</h1>
              <p className={cn('text-xs mt-0.5 truncate', colors.textMuted)}>{formSubtitle}</p>
            </div>
          </div>
          <div className="flex items-center gap-2 shrink-0">
            <Button type="button" variant="outline" size="sm" onClick={goBack}>
              {moduleConfig.form.cancelText}
            </Button>
            <Button type="submit" size="sm" disabled={saving} loading={saving}>
              {submitText}
            </Button>
          </div>
        </div>

        <div className="flex-1 min-h-0 overflow-y-auto dashboard-scrollbar">
          <div className="grid grid-cols-1 lg:grid-cols-5 min-h-full">
            <aside
              className={cn(
                'lg:col-span-2 border-b lg:border-b-0 lg:border-r p-5 sm:p-6',
                colors.border,
                colors.surfaceHover,
              )}
            >
              <div className="space-y-4">
                <div>
                  <p className={cn(acctFieldLabel, 'mb-0', colors.text)}>
                    Live preview
                  </p>
                  <div
                    className={cn(
                      'mt-3 rounded-xl border px-4 py-5',
                      colors.border,
                      colors.surface,
                    )}
                  >
                    {selectedCurrency && formattedRate ? (
                      <div className="space-y-3">
                        <div className="flex items-baseline gap-2 flex-wrap">
                          <span className={cn('text-3xl font-semibold tabular-nums', colors.text)}>1</span>
                          <span className={cn('text-lg font-medium', colors.text)}>
                            {formatCurrencyCodeSymbol(selectedCurrency)}
                          </span>
                          <span className={cn('text-sm', colors.textMuted)}>=</span>
                          <span className={cn('text-3xl font-semibold tabular-nums', colors.primaryFg)}>
                            {formattedRate}
                          </span>
                          <span className={cn('text-lg font-medium', colors.textMuted)}>USD</span>
                        </div>
                        {exampleUsd != null && (
                          <p className={cn('text-sm tabular-nums', colors.textMuted)}>
                            {exampleLocal.toLocaleString()} {formatCurrencyCodeSymbol(selectedCurrency)} ≈{' '}
                            <span className={cn('font-medium', colors.text)}>
                              {exampleUsd.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })} USD
                            </span>
                          </p>
                        )}
                        <p className={cn('text-xs', colors.textMuted)}>
                          Effective {formatDateGlobal(rateDate)}
                        </p>
                      </div>
                    ) : (
                      <div className={cn('text-sm leading-relaxed', colors.textMuted)}>
                        Select a currency and enter a rate to see the USD conversion preview.
                      </div>
                    )}
                  </div>
                </div>

                <div className={cn('rounded-lg border px-4 py-3 text-sm leading-relaxed', colors.border, colors.surface)}>
                  <p className={cn('font-medium mb-1', colors.text)}>Rate convention</p>
                  <p className={colors.textMuted}>
                    Store <strong className={colors.text}>USD per 1 unit</strong> of the selected currency — the same
                    convention used on journal lines when posting foreign amounts.
                  </p>
                </div>

                <p className={cn('text-xs leading-relaxed', colors.textMuted)}>
                  One rate per currency and date. Posting a journal on this date will use this
                  rate when converting to USD.
                </p>
              </div>
            </aside>

            <section className="lg:col-span-3 p-5 sm:p-6">
              <div className="max-w-xl space-y-6">
                <div>
                  <h2 className={cn('text-sm font-semibold', colors.text)}>Exchange rate details</h2>
                  <p className={cn('text-xs mt-0.5', colors.textMuted)}>
                    Choose the currency, effective date, and USD rate.
                  </p>
                </div>

                <div>
                  <Label required>Currency</Label>
                  <Select
                    value={currencyId}
                    onChange={(e) => setCurrencyId(e.target.value)}
                    options={currencyOptions}
                    showClearButton={false}
                  />
                  <FieldHint>Foreign currency this rate applies to.</FieldHint>
                </div>

                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                  <div>
                    <Label required>Rate date</Label>
                    <DateInput
                      value={rateDate}
                      onChange={(e) => setRateDate(e.target.value)}
                      showFormatHint={false}
                    />
                    <FieldHint>Date journals will use this rate when converting amounts.</FieldHint>
                  </div>

                  <div>
                    <Label required>Rate (USD)</Label>
                    <div className="flex items-start gap-2">
                      <div className="flex-1 min-w-0">
                        <NumberInput
                          value={rate}
                          onChange={(e) => setRate(e.target.value == null ? '' : String(e.target.value))}
                          min={0}
                          step={0.000001}
                          allowDecimals
                          placeholder="e.g. 1.085"
                          suffix={<span className={cn('text-xs font-medium pr-1', colors.textMuted)}>USD</span>}
                        />
                      </div>
                      <Button
                        type="button"
                        variant="outline"
                        size="sm"
                        className="shrink-0 mt-0.5"
                        disabled={fetchingOnline || !currencyId || !rateDate}
                        loading={fetchingOnline}
                        onClick={handleFetchOnline}
                        title="Fetch rate online (ECB via Frankfurter)"
                      >
                        <RefreshIcon className="h-4 w-4" />
                        <span className="hidden sm:inline">Fetch online</span>
                      </Button>
                    </div>
                    <FieldHint>
                      How many US dollars equal one unit of the selected currency.
                      {onlineSourceDate && onlineSourceDate !== rateDate && (
                        <>
                          {' '}
                          Online source date: {formatDateGlobal(onlineSourceDate)}.
                        </>
                      )}
                    </FieldHint>
                  </div>
                </div>
              </div>
            </section>
          </div>
        </div>
      </form>
    </div>
  );
};
